/**
 * CampusReviewTable — Campus stats table with radio selection + shift breakdown columns.
 */

import { useState, useMemo, useCallback } from 'react';
import { exportWellnessCsv } from '../../../../services/csvExport.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import Pagination from '../../../../components/v2/Pagination.jsx';
import ShiftLegend from './ShiftLegend.jsx';
import SortableTh from '../../../../components/v2/SortableTh.jsx';

const CSV_COLS = [
    { key: 'campus_name', label: 'Campus' },
    { key: 'total_events', label: 'Total Events' },
    { key: 'avg_daily_events', label: 'Avg Daily' },
    { key: 'avg_response_time', label: 'Avg Response (min)' },
    { key: 'residents', label: 'Residents' },
];

const CampusReviewTable = ({ data, selectedCampusId, onSelectCampus, search, onSearchChange, shiftData }) => {
    const [sortCol, setSortCol] = useState('campus_name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    const filtered = useMemo(() => {
        let rows = Array.isArray(data) ? [...data] : [];
        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((r) => (r.campus_name || '').toLowerCase().includes(q));
        }
        rows.sort((a, b) => {
            const aV = a[sortCol] ?? '';
            const bV = b[sortCol] ?? '';
            if (typeof aV === 'number' && typeof bV === 'number') return sortDir === 'asc' ? aV - bV : bV - aV;
            const cmp = String(aV).localeCompare(String(bV), undefined, { numeric: true });
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data, search, sortCol, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

    const handleSort = useCallback((col) => {
        if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    }, [sortCol]);

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    return (
        <div className="md-section">
            <div className="md-section__header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <h3 className="md-section__title" style={{ margin: 0 }}>Campus Review (Last 28 Days)</h3>
                    <ShiftLegend shiftData={shiftData} />
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <div className="ap-search" style={{ minWidth: 200 }}>
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                        <input type="search" placeholder="Search campuses..." value={search} onChange={(e) => { onSearchChange(e.target.value); setPage(1); }} className="ap-search__input" />
                    </div>
                    {filtered.length > 0 ? (
                        <button className="rp-btn rp-btn--secondary" onClick={() => exportWellnessCsv(filtered, CSV_COLS, 'campus-review.csv')} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>
                            <i className="fe fe-download" style={{ marginRight: 4 }} />CSV
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="dp-table-wrap" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th" style={{ width: 40 }}></th>
                            <SortableTh sortKey="campus_name" sortColumn={sortCol} sortOrder={sortDir} onSort={handleSort} className="dp-th">Campus<SortIcon col="campus_name" /></SortableTh>
                            <SortableTh sortKey="total_events" sortColumn={sortCol} sortOrder={sortDir} onSort={handleSort} className="dp-th dp-th--center">Total Events<SortIcon col="total_events" /></SortableTh>
                            <SortableTh sortKey="avg_daily_events" sortColumn={sortCol} sortOrder={sortDir} onSort={handleSort} className="dp-th dp-th--center">Avg Daily<SortIcon col="avg_daily_events" /></SortableTh>
                            <SortableTh sortKey="avg_response_time" sortColumn={sortCol} sortOrder={sortDir} onSort={handleSort} className="dp-th dp-th--center">Avg Response<SortIcon col="avg_response_time" /></SortableTh>
                            <SortableTh sortKey="residents" sortColumn={sortCol} sortOrder={sortDir} onSort={handleSort} className="dp-th dp-th--center">Residents<SortIcon col="residents" /></SortableTh>
                            <th className="dp-th md-shift--day dp-th--center">Day Events</th>
                            <th className="dp-th md-shift--day dp-th--center">Day Avg Resp</th>
                            <th className="dp-th md-shift--evening dp-th--center">Eve Events</th>
                            <th className="dp-th md-shift--evening dp-th--center">Eve Avg Resp</th>
                            <th className="dp-th md-shift--night dp-th--center">Night Events</th>
                            <th className="dp-th md-shift--night dp-th--center">Night Avg Resp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td dp-td--center" colSpan={12}><span className="dp-cell-text dp-cell-text--muted">No campus data</span></td></tr>
                        ) : null}
                        {pageRows.map((r) => (
                            <tr key={r.campus_id} className={`dp-tr${selectedCampusId === r.campus_id ? ' is-selected' : ''}`}>
                                <td className="dp-td"><div className="cd-radio-cell"><input type="radio" name="campus-select" checked={selectedCampusId === r.campus_id} onChange={() => onSelectCampus(r)} /></div></td>
                                <td className="dp-td" style={{ fontWeight: 500 }}>{r.campus_name || '—'}</td>
                                <td className="dp-td dp-td--center">{r.total_events ?? '—'}</td>
                                <td className="dp-td dp-td--center">{r.avg_daily_events ?? '—'}</td>
                                <td className="dp-td dp-td--center">{r.avg_response_time ?? '—'}</td>
                                <td className="dp-td dp-td--center">{r.residents ?? '—'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{r.total_events_1st ?? '-'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{r.avg_response_time_1st ?? '-'}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{r.total_events_2nd ?? '-'}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{r.avg_response_time_2nd ?? '-'}</td>
                                <td className="dp-td md-shift--night dp-td--center">{r.total_events_3rd ?? '-'}</td>
                                <td className="dp-td md-shift--night dp-td--center">{r.avg_response_time_3rd ?? '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {filtered.length > 0 ? (
                <Pagination
                    page={safePage}
                    totalPages={totalPages}
                    total={filtered.length}
                    perPage={perPage}
                    onPageChange={setPage}
                    onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                    entityLabel={filtered.length === 1 ? 'campus' : 'campuses'}
                    selectId="campus-review-per-page"
                />
            ) : null}
        </div>
    );
};

export default CampusReviewTable;
